"use client";

import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import type { ListPractitionersQuery, PractitionerPublicListDto } from "cmw-client";
import { browserCmw } from "@/lib/ky/browser";
import { uniquePractitioners } from "@/lib/unique-practitioners";

export const PUBLIC_PRACTITIONERS_QUERY_KEY = ["practitioners", "public"] as const;

const ONE_MINUTE = 60 * 1000;

export function usePublicPractitioner(username: string) {
  const query = useQuery({
    queryKey: [...PUBLIC_PRACTITIONERS_QUERY_KEY, username],
    queryFn: () => browserCmw.practitionersPublic.byUsername(username),
    enabled: username.length > 0,
    staleTime: ONE_MINUTE,
  });

  return {
    profile: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refresh: query.refetch,
  };
}

const nextOffset = (page: PractitionerPublicListDto): number | undefined => {
  const loaded = page.offset + page.items.length;

  return loaded < page.total ? loaded : undefined;
};

export function useInfinitePublicPractitioners(
  query: ListPractitionersQuery = {},
  pageSize = 10,
  /* Later pages may be larger than the first: offsets come from items actually loaded. */
  loadMorePageSize = pageSize,
) {
  const infiniteQuery = useInfiniteQuery({
    queryKey: [...PUBLIC_PRACTITIONERS_QUERY_KEY, "infinite", pageSize, loadMorePageSize, query],
    queryFn: ({ pageParam }) =>
      browserCmw.practitionersPublic.list({
        ...query,
        offset: pageParam,
        limit: pageParam === 0 ? pageSize : loadMorePageSize,
      }),
    initialPageParam: 0,
    getNextPageParam: nextOffset,
    staleTime: ONE_MINUTE,
  });

  return {
    practitioners: uniquePractitioners(infiniteQuery.data?.pages ?? []),
    total: infiniteQuery.data?.pages[0]?.total ?? 0,
    hasMore: infiniteQuery.hasNextPage,
    loadMore: infiniteQuery.fetchNextPage,
    isLoadingMore: infiniteQuery.isFetchingNextPage,
    isLoading: infiniteQuery.isLoading,
    isError: infiniteQuery.isError,
    error: infiniteQuery.error,
    refresh: infiniteQuery.refetch,
  };
}

export type UseInfinitePublicPractitionersResult = ReturnType<typeof useInfinitePublicPractitioners>;
